import { useCallback, useEffect, useState } from 'react'
import { prefetchAccess } from '../sync/access'
import { getMeta, listCompletions, setMeta } from '../sync/db'
import { getDraft } from '../sync/drafts'
import { CHANNEL_NAME } from '../sync/engine'
import { getLocalOrder, loadOrFetchRoute, localStatus, moveNext, orderedStops, type DriverRoute, type DriverStop, type LocalStatus, type RouteSource } from '../sync/route'
import type { CompletionRecord } from '../sync/types'
import { badge, bigButton, button, card, color, container, fmtDate, fmtTime, footer, h1, muted, page, textButton, tierColor, tierLabel } from '../ui'

/**
 * ROUTE LIST (v3 section 9): ordered stops as big cards - name, address,
 * item count, tier badge. Completed stops collapse. "Fix last stop" is
 * always there. "Do this next" pulls a stop forward; the server learns the
 * actual order from each completion, never from this screen.
 */
export function RouteScreen({
  onOpenStop,
  onFixStop,
  onSignOut,
}: {
  onOpenStop: (stop: DriverStop) => void
  onFixStop: (stop: DriverStop, rec: CompletionRecord) => void
  onSignOut: () => void
}) {
  const [route, setRoute] = useState<DriverRoute | null>(null)
  const [source, setSource] = useState<RouteSource>('none')
  const [loadError, setLoadError] = useState('')
  const [order, setOrder] = useState<string[]>([])
  const [completions, setCompletions] = useState<CompletionRecord[]>([])
  const [drafts, setDrafts] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [showDone, setShowDone] = useState(false)
  const [accessNote, setAccessNote] = useState('')

  const refreshLocal = useCallback(async (r: DriverRoute | null) => {
    const cs = await listCompletions()
    setCompletions(cs)
    if (r) {
      setOrder(await getLocalOrder(r.id))
      const d = new Set<string>()
      for (const s of r.stops) if (await getDraft(s.uuid)) d.add(s.uuid)
      setDrafts(d)
    }
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    const res = await loadOrFetchRoute()
    setRoute(res.route)
    setSource(res.source)
    setLoadError(res.error ?? '')
    await refreshLocal(res.route)
    setLoading(false)
    if (res.route && res.source === 'server') {
      const already = await getMeta<number>(`access-prefetched:${res.route.id}`)
      // Refresh the facts at most every 30 minutes while online.
      if (!already || Date.now() - already > 30 * 60_000) {
        const { ok, failed } = await prefetchAccess(res.route.stops.map((s) => s.uuid))
        await setMeta(`access-prefetched:${res.route.id}`, Date.now())
        if (failed) setAccessNote(`${ok} of ${ok + failed} stops' access notes cached`)
      }
    }
  }, [refreshLocal])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    let ch: BroadcastChannel | null = null
    try {
      ch = new BroadcastChannel(CHANNEL_NAME)
      ch.onmessage = () => void refreshLocal(route)
    } catch {
      /* no channel */
    }
    const t = window.setInterval(() => void refreshLocal(route), 4000)
    return () => {
      ch?.close()
      window.clearInterval(t)
    }
  }, [route, refreshLocal])

  const signOut = () => onSignOut()

  if (loading && !route) {
    return (
      <div style={page}>
        <div style={container}>
          <div style={h1}>Loading your route…</div>
        </div>
      </div>
    )
  }

  if (!route) {
    return (
      <div style={page}>
        <div style={container}>
          <div style={h1}>No route today</div>
          <div style={{ ...muted, marginBottom: 16 }}>
            {source === 'none' && loadError ? `Could not reach the server (${loadError}) and nothing is cached.` : 'Nothing has been dispatched to you yet.'}
          </div>
          <button style={button(color.green)} onClick={load}>Check again</button>
          <div style={{ marginTop: 24 }}>
            <button style={textButton} onClick={signOut}>Sign out</button>
          </div>
        </div>
      </div>
    )
  }

  const byUuid = new Map(completions.map((c) => [c.stopUuid, c]))
  const stops = orderedStops(route, order)
  const status = (s: DriverStop): LocalStatus => localStatus(s, byUuid.get(s.uuid), drafts.has(s.uuid))
  const pending = stops.filter((s) => ['pending', 'in_progress'].includes(status(s)))
  const done = stops.filter((s) => !['pending', 'in_progress'].includes(status(s)))
  const last = completions
    .filter((c) => c.routeId === route.id)
    .sort((a, b) => b.updatedAt - a.updatedAt)[0]
  const lastStop = last ? route.stops.find((s) => s.uuid === last.stopUuid) : undefined

  const doNext = async (s: DriverStop) => {
    setOrder(await moveNext(route, s.uuid))
  }

  return (
    <div style={page}>
      <div style={container}>
        <div style={h1}>{fmtDate(route.route_date)}</div>
        <div style={{ ...muted, marginBottom: 4 }}>
          {done.length} of {route.stops.length} done
          {source === 'cache' && <span style={{ color: color.amber, fontWeight: 700 }}> · offline, showing saved route</span>}
        </div>
        {accessNote && <div style={{ ...muted, color: color.amber, marginBottom: 8 }}>{accessNote}</div>}

        {pending.length === 0 && (
          <div style={{ ...card, background: '#ecfdf5', borderColor: '#6ee7b7' }}>
            <div style={{ fontSize: 22, fontWeight: 800 }}>All stops done.</div>
            <div style={muted}>Keep the app open until the upload bar says everything is uploaded.</div>
          </div>
        )}

        {pending.map((s, i) => {
          const st = status(s)
          return (
            <div key={s.uuid} style={{ ...card, borderWidth: i === 0 ? 3 : 1, borderColor: i === 0 ? tierColor(s.service_tier) : color.greyLight }}>
              <div onClick={() => onOpenStop(s)} role="button" style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <span style={{ fontSize: 20, fontWeight: 800, color: color.grey }}>{i === 0 ? 'NEXT' : `#${i + 1}`}</span>
                  <span style={badge(tierColor(s.service_tier))}>{tierLabel(s.service_tier)}</span>
                  {st === 'in_progress' && <span style={badge(color.amber)}>IN PROGRESS</span>}
                </div>
                <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.2 }}>{s.name}</div>
                <div style={{ fontSize: 18, marginTop: 2 }}>{s.address}, {s.city}</div>
                <div style={{ ...muted, marginTop: 4 }}>
                  {s.item_count} bag{s.item_count === 1 ? '' : 's'} · {s.checklist_required.length} checklist items
                  {s.planned_eta ? ` · planned ${fmtTime(s.planned_eta)}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <button style={{ ...button(tierColor(s.service_tier)), flex: 1 }} onClick={() => onOpenStop(s)}>
                  {st === 'in_progress' ? 'Continue' : 'Open stop'}
                </button>
                {i > 0 && (
                  <button style={button(color.white, color.ink)} onClick={() => doNext(s)}>
                    Do this next
                  </button>
                )}
              </div>
            </div>
          )
        })}

        {done.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <button style={textButton} onClick={() => setShowDone((v) => !v)}>
              {showDone ? 'Hide' : 'Show'} {done.length} completed stop{done.length === 1 ? '' : 's'}
            </button>
            {showDone &&
              done.map((s) => {
                const st = status(s)
                const rec = byUuid.get(s.uuid)
                return (
                  <div key={s.uuid} style={{ ...card, padding: '10px 14px', opacity: 0.85 }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={badge(st === 'skipped' ? color.amber : color.green)}>{st === 'skipped' ? 'SKIPPED' : 'DONE'}</span>
                      <span style={{ fontWeight: 700, fontSize: 18 }}>{s.name}</span>
                      <span style={muted}>{rec?.payload.actual_sequence ? `#${rec.payload.actual_sequence}` : ''}</span>
                      {rec && rec.state !== 'synced' && <span style={{ ...muted, color: color.amber }}>· not uploaded yet</span>}
                    </div>
                  </div>
                )
              })}
          </div>
        )}

        <div style={{ marginTop: 24, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button style={textButton} onClick={load}>Refresh route</button>
          <button style={textButton} onClick={signOut}>Sign out</button>
        </div>
      </div>

      {lastStop && last && (
        <div style={footer}>
          <button style={{ ...bigButton(color.white), color: color.ink, border: `3px solid ${color.ink}`, boxShadow: 'none' }} onClick={() => onFixStop(lastStop, last)}>
            Fix last stop · {lastStop.name}
          </button>
        </div>
      )}
    </div>
  )
}
